import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { Key, Users, Shield, Copy, Clock, LogIn, UserPlus, Power, PowerOff, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { API } from "./constants";

const SettingsAccessCodesTab = ({ lang = "en" }) => {
  const [accessCodes, setAccessCodes] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [loadingCodes, setLoadingCodes] = useState(true);
  const [codeDialogOpen, setCodeDialogOpen] = useState(false);
  const [newCodeName, setNewCodeName] = useState("");
  const [newCodeIsAdmin, setNewCodeIsAdmin] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoadingCodes(true);
      const [codesRes, historyRes] = await Promise.all([
        axios.get(`${API}/auth/codes`),
        axios.get(`${API}/auth/login-history?limit=50`),
      ]);
      setAccessCodes(codesRes.data.codes || []);
      setLoginHistory(historyRes.data.history || []);
    } catch {
      // silent
    } finally {
      setLoadingCodes(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createAccessCode = async () => {
    if (!newCodeName.trim()) { toast.error(lang === "ko" ? "이름을 입력해주세요" : "Enter a name"); return; }
    try {
      const response = await axios.post(`${API}/auth/codes`, { name: newCodeName.trim(), is_admin: newCodeIsAdmin });
      toast.success(lang === "ko" ? "액세스 코드가 생성되었습니다" : "Access code created");
      setCodeDialogOpen(false);
      setNewCodeName("");
      setNewCodeIsAdmin(false);
      fetchData();
      if (response.data.code?.code) toast.info(`${lang === "ko" ? "새 코드" : "New code"}: ${response.data.code.code}`, { duration: 10000 });
    } catch (error) { toast.error(error.response?.data?.detail || (lang === "ko" ? "코드 생성 실패" : "Failed to create code")); }
  };

  const toggleCodeActive = async (code, isActive) => {
    try {
      if (isActive) { await axios.delete(`${API}/auth/codes/${encodeURIComponent(code)}`); toast.success(lang === "ko" ? "코드가 비활성화되었습니다" : "Code deactivated"); }
      else { await axios.put(`${API}/auth/codes/${encodeURIComponent(code)}/activate`); toast.success(lang === "ko" ? "코드가 활성화되었습니다" : "Code activated"); }
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || (lang === "ko" ? "상태 변경 실패" : "Failed to toggle")); }
  };

  const copyToClipboard = (text) => { navigator.clipboard.writeText(text); toast.success(lang === "ko" ? "클립보드에 복사되었습니다" : "Copied to clipboard"); };

  if (loadingCodes) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="space-y-4">
      <Card className="bg-card border-border/50">
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="text-lg font-heading flex items-center gap-2"><Key className="h-5 w-5" />{lang === "ko" ? "액세스 코드 관리" : lang === "zh" ? "访问码管理" : "Access Code Management"}</CardTitle>
              <CardDescription>{lang === "ko" ? "사용자 액세스 코드를 관리합니다" : lang === "zh" ? "管理用户访问码" : "Manage user access codes"}</CardDescription>
            </div>
            <Dialog open={codeDialogOpen} onOpenChange={setCodeDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="w-full sm:w-auto"><UserPlus className="h-4 w-4 mr-2" />{lang === "ko" ? "새 코드 생성" : lang === "zh" ? "创建新码" : "Create Code"}</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{lang === "ko" ? "새 액세스 코드 생성" : "Create New Access Code"}</DialogTitle><DialogDescription>{lang === "ko" ? "새로운 사용자를 위한 액세스 코드를 생성합니다" : "Create an access code for a new user"}</DialogDescription></DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2"><Label>{lang === "ko" ? "사용자 이름" : "User Name"}</Label><Input placeholder={lang === "ko" ? "예: 마케팅팀, 홍길동" : "e.g., Marketing Team"} value={newCodeName} onChange={(e) => setNewCodeName(e.target.value)} /></div>
                  <div className="flex flex-wrap items-center gap-2"><Switch checked={newCodeIsAdmin} onCheckedChange={setNewCodeIsAdmin} /><Label>{lang === "ko" ? "관리자 권한 부여" : "Grant Admin Access"}</Label></div>
                </div>
                <DialogFooter><Button variant="outline" onClick={() => setCodeDialogOpen(false)}>{lang === "ko" ? "취소" : "Cancel"}</Button><Button onClick={createAccessCode}>{lang === "ko" ? "생성" : "Create"}</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground mb-4">
              {lang === "ko" ? `총 ${accessCodes.length}개의 액세스 코드 | 활성: ${accessCodes.filter(c => c.is_active).length}개` : `Total ${accessCodes.length} access codes | Active: ${accessCodes.filter(c => c.is_active).length}`}
            </p>
            {accessCodes.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">{lang === "ko" ? "액세스 코드가 없습니다" : "No access codes"}</div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {accessCodes.map((code) => (
                  <div key={code.code} className={`flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between ${code.is_active ? "bg-card border-border/50" : "bg-muted/30 border-border/30 opacity-60"}`}>
                    <div className="flex min-w-0 items-center gap-3">
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center ${code.is_admin ? "bg-amber-500/20" : "bg-primary/20"}`}>
                        {code.is_admin ? <Shield className="h-4 w-4 text-amber-500" /> : <Users className="h-4 w-4 text-primary" />}
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{code.name}</span>
                          {code.is_admin && <Badge variant="outline" className="text-amber-500 border-amber-500">Admin</Badge>}
                          {!code.is_active && <Badge variant="secondary">{lang === "ko" ? "비활성" : "Inactive"}</Badge>}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                          <code className="max-w-full overflow-hidden text-ellipsis rounded bg-muted px-1.5 py-0.5 font-mono">{code.code}</code>
                          <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => copyToClipboard(code.code)}><Copy className="h-3 w-3" /></Button>
                        </div>
                      </div>
                    </div>
                    <div className="flex w-full items-center justify-between gap-4 sm:w-auto sm:justify-end">
                      <div className="text-xs text-muted-foreground sm:text-right">
                        <div className="flex items-center gap-1 sm:justify-end"><LogIn className="h-3 w-3" />{code.login_count || 0}{lang === "ko" ? "회" : "x"}</div>
                        {code.last_login_at && <div className="flex items-center gap-1 sm:justify-end"><Clock className="h-3 w-3" />{format(new Date(code.last_login_at), "MM/dd HH:mm")}</div>}
                      </div>
                      {code.code !== "A2O-ADMIN-2026-MASTER" && (
                        <Button variant="ghost" size="icon" className={code.is_active ? "text-red-400 hover:text-red-300" : "text-green-400 hover:text-green-300"} onClick={() => toggleCodeActive(code.code, code.is_active)}>
                          {code.is_active ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Login History */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2"><Clock className="h-5 w-5" />{lang === "ko" ? "로그인 기록" : lang === "zh" ? "登录记录" : "Login History"}</CardTitle>
          <CardDescription>{lang === "ko" ? "최근 50건의 로그인 기록" : "Last 50 login records"}</CardDescription>
        </CardHeader>
        <CardContent>
          {loginHistory.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">{lang === "ko" ? "로그인 기록이 없습니다" : "No login history"}</div>
          ) : (
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {loginHistory.map((log, index) => (
                <div key={index} className="flex flex-col gap-2 rounded border border-border/30 p-2 text-sm sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-wrap items-center gap-2">
                    {log.is_admin ? <Shield className="h-4 w-4 text-amber-500" /> : <Users className="h-4 w-4 text-muted-foreground" />}
                    <span className="font-medium">{log.name}</span>
                  </div>
                  <div className="text-muted-foreground text-xs">{format(new Date(log.login_at), "yyyy-MM-dd HH:mm:ss")}</div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsAccessCodesTab;
